"use client";
import { useState, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { useWatch, useForm } from "react-hook-form";
import PersonasApi from "@/api/Personas";
import SearchPersona from "@/components/Persona/SearchPersona";
import gustavoToJSON from "@/utils/gustavoToJSON";
import tiposTransaccion from "../data/tiposTransaccion.json";
import tipoLiquidacion from "../data/tiposLiquidacion.json";

const Step6 = forwardRef(({ onSubmitForm, isLoading, initialValues }, ref) => {
  const {
    register,
    setValue,
    getValues,
    control
  } = useForm({
    defaultValues: initialValues || {
      fechaAdquisicion: new Date().toISOString().split("T")[0],
      valorCompra: "",
      valorVenta: "",
      tipoTransaccion: "Compraventa",
      iess: false,
      identificacion: "",
      nombrePersona: "",
      idComprador: "",
      estadoCuenta: "",
      tipoLiquidacion: 0,
      mejoraPropia: "",
      identificacionComprador: "",
    },
  });

  const iess = useWatch({ control, name: "iess" });

  const [validationErrors, setValidationErrors] = useState({
    nombrePersona: false,
    fechaAdquisicion: false,
    valorCompra: false,
    valorVenta: false,
    mejoraPropia: false,
  });

  useImperativeHandle(ref, () => ({
    getValues: () => getValues(),
    validateAndSubmit: async () => {
      const currentValues = getValues();
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const fechaSeleccionada = currentValues.fechaAdquisicion ? new Date(currentValues.fechaAdquisicion) : null;

      const errors = {
        nombrePersona: !currentValues.nombrePersona,
        fechaAdquisicion: !currentValues.fechaAdquisicion || (fechaSeleccionada && fechaSeleccionada > hoy),
        valorCompra: currentValues.valorCompra === "" || currentValues.valorCompra < 0,
        valorVenta: currentValues.valorVenta === "" || currentValues.valorVenta < 0,
        mejoraPropia: currentValues.mejoraPropia === "" || currentValues.mejoraPropia < 0,
      };

      setValidationErrors(errors);

      if (Object.values(errors).some(error => error)) {
        return null;
      }

      if (onSubmitForm) onSubmitForm(currentValues);
      return currentValues;
    },
  }));

  return (
    <div className="dark:border-strokedark dark:bg-boxdark">
      {isLoading ? (
        <div className="flex h-full flex-col items-center justify-center text-center">
          <div className="mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
          <p className="select-none text-lg font-medium text-gray-700">
            Calculando...
          </p>
        </div>
      ) : (
        <div>
          <div className="mb-4.5">
            <label className="mb-2.5 block text-black dark:text-white">
              Buscar Comprador
            </label>
            <SearchPersona
              onSearch={async ({ identification }) => {
                const response = await PersonasApi.getDatos(identification);
                const data = gustavoToJSON(response.data.data);
                return { ...data, identification };
              }}
              onResult={(persona) => {
                setValue("nombrePersona", persona.nombre || "", { shouldValidate: true });
                setValue("idComprador", persona.idpersona || "");
                setValue("identificacionComprador", persona.identification || "");
                setValue("estadoCuenta", persona.estadocuenta || "");
                if (validationErrors.nombrePersona) {
                  setValidationErrors(prev => ({ ...prev, nombrePersona: false }));
                }
              }}
            />
          </div>
          <div className="mb-4.5">
            <label className="mb-2.5 block text-black dark:text-white">
              Nombre del Comprador
            </label>
            <input
              type="text"
              readOnly
              className={`w-full rounded border-[1.5px] ${validationErrors.nombrePersona ? "border-red-500" : "border-stroke"
                } bg-transparent px-5 py-3 font-medium outline-none`}
              {...register("nombrePersona")}
            />
            {validationErrors.nombrePersona && (
              <p className="text-red-500 text-sm mt-1 font-medium">
                El nombre del comprador es requerido
              </p>
            )}
          </div>

          <div className="mb-4.5 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div>
              <label className="mb-2.5 block">Fecha de adquisición *</label>
              <input
                type="date"
                max={new Date().toISOString().split("T")[0]}
                {...register("fechaAdquisicion")}
                className={`w-full rounded border-[1.5px] ${validationErrors.fechaAdquisicion ? "border-red-500" : "border-stroke"
                  } px-5 py-3`}
                onChange={() => {
                  if (validationErrors.fechaAdquisicion) {
                    setValidationErrors(prev => ({ ...prev, fechaAdquisicion: false }));
                  }
                }}
              />
              {validationErrors.fechaAdquisicion && (
                <p className="text-red-500 text-sm mt-1">
                  La fecha de adquisición no puede ser mayor a la fecha de hoy
                </p>
              )}
            </div>
            <div>
              <label className="mb-2.5 block">Tipo de transacción *</label>
              <select
                {...register("tipoTransaccion")}
                className="w-full rounded border-[1.5px] border-stroke px-5 py-3"
              >
                {tiposTransaccion.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
          </div>


          <div className="mb-4.5 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div>
              <label className="mb-2.5 block">Inversiones en el predio ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register("mejoraPropia")}
                className={`w-full rounded border-[1.5px] ${validationErrors.mejoraPropia ? "border-red-500" : "border-stroke"
                  } px-5 py-3`}
                onChange={() => {
                  if (validationErrors.mejoraPropia) {
                    setValidationErrors(prev => ({ ...prev, mejoraPropia: false }));
                  }
                }}
              />
              {validationErrors.mejoraPropia && (
                <p className="text-red-500 text-sm mt-1">
                  Las inversiones en el predio deben ser mayores o iguales a 0
                </p>
              )}
            </div>
            <div>
              <label className="mb-2.5 block">Tipo de liquidación *</label>
              <select
                {...register("tipoLiquidacion")}
                className="w-full rounded border-[1.5px] border-stroke px-5 py-3"
              >
                {tipoLiquidacion.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4.5 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div>
              <label className="mb-2.5 block">Valor de compra ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register("valorCompra")}
                className={`w-full rounded border-[1.5px] ${validationErrors.valorCompra ? "border-red-500" : "border-stroke"
                  } px-5 py-3`}
                onChange={() => {
                  if (validationErrors.valorCompra) {
                    setValidationErrors(prev => ({ ...prev, valorCompra: false }));
                  }
                }}
              />
              {validationErrors.valorCompra && (
                <p className="text-red-500 text-sm mt-1">
                  El valor de compra debe ser mayor o igual a 0
                </p>
              )}
            </div>
            <div>
              <label className="mb-2.5 block">Valor de venta ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register("valorVenta")}
                className={`w-full rounded border-[1.5px] ${validationErrors.valorVenta ? "border-red-500" : "border-stroke"
                  } px-5 py-3`}
                onChange={() => {
                  if (validationErrors.valorVenta) {
                    setValidationErrors(prev => ({ ...prev, valorVenta: false }));
                  }
                }}
              />
              {validationErrors.valorVenta && (
                <p className="text-red-500 text-sm mt-1">
                  El valor de venta debe ser mayor o igual a 0
                </p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center cursor-pointer relative">
              <input
                type="checkbox"
                className="sr-only"
                {...register("iess")}
              />
              <div className="block h-6 w-12 rounded-full bg-black/20"></div>
              <div
                className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${iess && "!translate-x-6 !bg-primary"
                  }`}
              />
              <span className="ml-3.5">Venta a través del IESS</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
});

Step6.displayName = "Step6";
Step6.propTypes = {
  onSubmitForm: PropTypes.func,
  isLoading: PropTypes.bool,
  initialValues: PropTypes.object
};

export default Step6;
